/**
 * MappingOutcomesLegend — a small, always-visible summary at the top of the
 * per-file mapping view stating plainly what happens to mapped, proposed
 * (new-property), and unmapped columns, and how create vs update works.
 *
 * This exists because the same explanation used to live only in hover-only
 * tooltips scattered across the mapping widgets (the record-matching toggle,
 * the "Add new property" dropdown action, the Unassigned bucket). A user who
 * never hovers never learns what the import will actually do. This component
 * doesn't replace those tooltips — it restates their substance up front, in
 * the exact on-screen wording, so the summary matches the widgets.
 */
import { InfoCircle } from '@untitledui/icons';

import { useImportVocabulary } from '../../import-vocabulary';

interface MappingOutcomesLegendProps {
  /** Whether the current user can create new CRM properties (schema admin). */
  canCreateNewProperties: boolean;
}

export function MappingOutcomesLegend({
  canCreateNewProperties,
}: MappingOutcomesLegendProps) {
  const { columnNoun, columnNounPlural } = useImportVocabulary();

  return (
    <div
      data-testid='mapping-outcomes-legend'
      className='rounded-lg border border-[var(--color-border-secondary)] bg-[var(--color-bg-secondary)] px-4 py-3'
    >
      <div className='flex items-center gap-1.5'>
        <InfoCircle
          aria-hidden={true}
          className='w-3.5 h-3.5 text-fg-quaternary'
        />
        <h3 className='text-sm font-medium text-[var(--color-text-primary)]'>
          What this import will do
        </h3>
      </div>
      <ul className='mt-2 space-y-1.5 text-sm text-[var(--color-text-secondary)]'>
        <li>
          <span className='font-medium text-[var(--color-text-primary)]'>
            Mapped {columnNounPlural}
          </span>{' '}
          import into the CRM property you choose. Each row is set to Create
          new, or Update on match when you change Record matching.
        </li>
        <li>
          {canCreateNewProperties ? (
            <>
              <span className='font-medium text-[var(--color-text-primary)]'>
                New properties
              </span>{' '}
              aren&apos;t created unless you add them. Choose Add new property
              on a {columnNoun} to create one; it shows a New badge and is
              listed under new properties.
            </>
          ) : (
            <>
              <span className='font-medium text-[var(--color-text-primary)]'>
                New properties
              </span>{' '}
              can&apos;t be created in this import.
            </>
          )}
        </li>
        <li>
          <span className='font-medium text-[var(--color-text-primary)]'>
            Unmapped {columnNounPlural}
          </span>{' '}
          stay in Unassigned and aren&apos;t imported.
        </li>
      </ul>
    </div>
  );
}
